import React, { useState, useEffect } from 'react';
import { Sparkles, Check, AlertCircle, RefreshCw } from 'lucide-react';

export function SettingsModal({ isOpen, onClose, onSave }) {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const storedKey = localStorage.getItem('magnific_gemini_api_key') || '';
      const storedModel = localStorage.getItem('magnific_gemini_model') || 'gemini-2.5-flash';
      setApiKey(storedKey);
      setModel(storedModel);
      setTestResult(null);
      setSaved(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    localStorage.setItem('magnific_gemini_api_key', apiKey.trim());
    localStorage.setItem('magnific_gemini_model', model);
    setSaved(true);
    if (onSave) onSave({ apiKey: apiKey.trim(), model });
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 800);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/prompt/enhance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: 'test connection', customApiKey: apiKey.trim(), model })
      });
      const data = await res.json();
      if (data.success) {
        setTestResult({ success: true, message: 'Conexión con Google Gemini exitosa' });
      } else {
        setTestResult({ success: false, message: data.error || 'Clave de API inválida' });
      }
    } catch {
      setTestResult({ success: true, message: 'Modo local activo (simulación cinemática lista)' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100
    }}>
      <div style={{
        width: '460px',
        background: '#0f1118',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.9), 0 0 30px rgba(168,85,247,0.15)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={16} color="#c084fc" />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#f8fafc' }}>Configuración del Estudio</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>Google Gemini & Veo Engine</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
              Google Gemini API Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="AIzaSy..."
              style={{
                width: '100%',
                background: '#141724',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                padding: '10px 12px',
                color: '#f8fafc',
                fontSize: '13px',
                outline: 'none',
                fontFamily: 'monospace'
              }}
            />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
              Permite usar Gemini 2.5 Pro/Flash directamente para optimización de prompts y generación con Veo.
            </div>
          </div>

          <div>
            <label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
              Modelo de IA Predeterminado
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={{
                width: '100%',
                background: '#141724',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                padding: '10px 12px',
                color: '#f8fafc',
                fontSize: '13px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="gemini-2.5-flash">Gemini 2.5 Flash (Ultra Rápido & Creativo)</option>
              <option value="gemini-2.5-pro">Gemini 2.5 Pro (Máximo detalle cinematográfico)</option>
              <option value="veo-2.0-preview">Google Veo 2.0 (Motor de Video de Avatar)</option>
            </select>
          </div>

          {testResult && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              background: testResult.success ? 'rgba(16,185,129,0.1)' : 'rgba(244,63,94,0.1)',
              color: testResult.success ? '#10b981' : '#f43f5e',
              border: `1px solid ${testResult.success ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)'}`
            }}>
              {testResult.success ? <Check size={14} /> : <AlertCircle size={14} />}
              <span>{testResult.message}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testing}
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#cbd5e1',
              padding: '8px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RefreshCw size={13} className={testing ? 'spin' : ''} />
            {testing ? 'Probando...' : 'Test de API'}
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#94a3b8',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              style={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                border: 'none',
                color: '#fff',
                padding: '8px 18px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(168,85,247,0.3)'
              }}
            >
              {saved ? <Check size={14} /> : null}
              {saved ? '¡Guardado!' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
